import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Collapse, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MyCafeteriaBenefitForm } from "my-cafeteria/components";
import {
  MyCafateriaBenefitsMobileTableDef,
  myCafateriaBenefitsTableDef,
} from "my-cafeteria/configs";
import { MY_CAFETERIA_APPROVE_CURRENT_STATEMENT } from "my-cafeteria/graphql";
import { useMyCafeteriaCurrentStatementItems } from "my-cafeteria/hooks";
import {
  myCafateriaNewStatementItemState,
  myCafateriaShowNewStatementItemFormState,
  myCafeteriaShowNewStatementItemInlineFormState,
} from "my-cafeteria/states";
import { OcCard, OcConfirmationDialog, OcDataTable } from "oc/components";
import { useOcSnackbar } from "oc/hooks";

interface MyCafeteriaBenefitsCardProps {
  contractCode: string;
  currentStatement: any; // Replace 'any' with the actual type if available
  isMobile: boolean;
}

export const MyCafeteriaBenefitsCard = ({
  contractCode,
  currentStatement,
  isMobile,
}: MyCafeteriaBenefitsCardProps) => {
  /** Translation */
  const { t } = useTranslation();
  const theme = useTheme();
  const { setOpenMessage } = useOcSnackbar();

  /** Used states in the component */
  /** Store new end edited items */
  const [newItem, setNewItem] = useRecoilState(
    myCafateriaNewStatementItemState,
  );
  /** Open and close edit forms state */
  const [showNewItemForm, setShowNewItemForm] = useRecoilState(
    myCafateriaShowNewStatementItemFormState,
  );
  /** If form is inline edit store the opned/closed state*/
  const [showInlineEditForm, setShowInlineEditForm] = useRecoilState(
    myCafeteriaShowNewStatementItemInlineFormState,
  );

  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

  const { data, loading, error } = useMyCafeteriaCurrentStatementItems({
    contractCode,
  });

  const list = {
    rows: data,
  };

  /** Define editable state to aloow/disallow editing an item */
  const editable = currentStatement?.state === "editable" ? true : false;

  /** Mutation for closing the current application by the user */
  const [approveCurrentCafeteriaStatement] = useMutation(MY_CAFETERIA_APPROVE_CURRENT_STATEMENT, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("OC_OPERATION_SUCCESSFUL"),
      });
    },
    onError: (error) => {
      console.log({ error });
      setOpenMessage({
        type: "error",
        message: t("OC_OPERATION_UNSUCCESSFUL"),
      });
    },
  },
  );

  /** Event handlers */
  const handleOpenShowNewItemForm = () => {
    setShowNewItemForm(true);
  };

  const handleCloseShowNewItemForm = () => {
    setShowNewItemForm(false);
  };

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const handleSelectRow = ({ item }: { item: any }) => {
    const {
      amount,
      contractCode,
      startDate,
      endDate,
      menuItem,
      type,
      index,
      szepCardApplicationAmount,
      numberOfMonths,
    } = item;
    let parsedAmount = amount / numberOfMonths;
    let newState = {
      index,
      amount: parsedAmount,
      contractCode,
      startDate,
      endDate,
      menuItemCode: menuItem.code,
      benefitType: menuItem.code,
      type,
      isPeriodical: menuItem.isPeriodical,
      szepCardApplicationAmount,
    };
    setNewItem((state: any) => {
      let result = Object.assign({}, state, newState);
      return result;
    });
    setShowInlineEditForm(true);
  };

  const onSelectRow = (item: any) => {
    if (isMobile) {
      if (item?.action === "edit") {
        handleSelectRow(item);
        setTimeout(() => {
          setSelectedRowId("");
        }, 0);
      } else {
        setSelectedRowId(selectedRowId === item?._id ? null : item?._id);
      }
    } else {
      handleSelectRow(item);
    }
  };

  const tableDef = isMobile
    ? MyCafateriaBenefitsMobileTableDef
    : myCafateriaBenefitsTableDef;

  const filteredTableDef = {
    id: myCafateriaBenefitsTableDef?.id,
    columns: myCafateriaBenefitsTableDef?.columns?.filter(
      (column) =>
        !MyCafateriaBenefitsMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const handleCloseInlineEditingForm = () => {
    setShowInlineEditForm(false);
  };

  const handleShowInlineEditForm = (row: any) => {
    const { index } = row || {};
    if (newItem?.index === index) {
      return !!showInlineEditForm;
    }
    return false;
  };

  const handleClose = () => {
    setShowConfirmDialog(false);
    let params = {
      contractCode,
      currentItemStartDate: currentStatement?.startDate,
    };
    approveCurrentCafeteriaStatement({ variables: params });
  };

  /** Define inline edit form */
  const InlineEditForm = (
    <MyCafeteriaBenefitForm
      onCloseForm={handleCloseInlineEditingForm}
      contractCode={contractCode}
      inlineEdit={true}
      currentStatement={currentStatement}
      currentStatementItems={data}
      isMobile={isMobile}
    />
  );

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <Box id="my-cafeteria-benefits-card">
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleClose}
        onCancel={handleCancel}
        title={t("MY_CAFETERIA_CONFIRM_TITLE")}
        texts={[t("MY_CAFETERIA_CONFIRM_TEXT")]}
        okButtonText={t("OC_FINISH")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("MY_CAFETERIA_ALL_AMOUNT")} error={error ? true : false}>
        <Box>
          <Box padding="15px 0px 41px 0px" textAlign="right">
            <Button
              id="my-cafeteria-benefits-card-new-benefit-add-button"
              variant="contained"
              color="primary"
              onClick={handleOpenShowNewItemForm}
              disabled={showNewItemForm || showInlineEditForm || !editable}
              disableElevation
            >
              {t("MY_CAFETERIA_NEW_BENEFIT")}
            </Button>
          </Box>
          <Collapse
            id="my-cafeteria-new-benefits-card"
            in={showNewItemForm}
            timeout="auto"
            unmountOnExit
          >
            <MyCafeteriaBenefitForm
              onCloseForm={handleCloseShowNewItemForm}
              contractCode={contractCode}
              currentStatement={currentStatement}
              currentStatementItems={data}
              isMobile={isMobile}
            />
            <Box height="32px" />
          </Collapse>
          <OcDataTable
            tableDef={tableDef}
            data={list}
            loading={showInlineEditForm ? false : loading}
            onSelectRow={onSelectRow}
            inlineEditForm={InlineEditForm}
            inlineEdit={!showNewItemForm && !showInlineEditForm && editable}
            onShowInlineEditForm={handleShowInlineEditForm}
            filteredTableDef={filteredTableDef}
            handleMoreButton={handleSelectRow}
            selectedRowId={selectedRowId}
            examinerDasboard={false}
            isNotExaminer={true}
            localPaging={false}
            matchingMyTimeitems={[]}
            tableId="my-cafeteria-benefits-table"
            pagination={false} // Added required prop
            tableData={[]} // Added required prop
          // Add other required props with default or suitable values as needed
          />
          {error && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              marginTop="16px"
              marginBottom="0px"
            >
              <Typography
                variant="body1"
                style={{ color: theme.palette.primary.main }}
              >
                {t("MY_CAFETERIA_DATA_FETCHING_ERROR")}
              </Typography>
            </Box>
          )}
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            padding="44px 0px 8px 0px"
          >
            <Button
              id="my-cafeteria-completion-button"
              variant="contained"
              color="primary"
              onClick={() => setShowConfirmDialog(true)}
              disabled={
                !editable ||
                (data && data?.length === 0) ||
                showInlineEditForm ||
                showNewItemForm
              }
              disableElevation
            >
              {t("MY_CAFETERIA_FINISH_APPLICATION")}
            </Button>
          </Box>
        </Box>
      </OcCard>
    </Box>
  );
};

export default MyCafeteriaBenefitsCard;
